import * as React from "react"
import { fetchActivationPackage } from "@/pages/legal-documents/api"
import {
  fetchConnections,
  fetchRun,
  fetchRuns,
  startPreview,
  startSync,
  type HrisConnection,
  type HrisRun,
} from "./hrisApi"

const POLL_MS = 3000

/**
 * The account's connection to one HR system, its latest run and whether the
 * Schedule E + employee-data permission are in place. A started run is polled
 * until it finishes, then the connection is reloaded (status, last sync).
 */
export function useHrisConnection(provider: string) {
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [connection, setConnection] = React.useState<HrisConnection | null>(null)
  const [run, setRun] = React.useState<HrisRun | null>(null)
  const [history, setHistory] = React.useState<HrisRun[]>([])
  const [legalComplete, setLegalComplete] = React.useState(false)
  const pollRef = React.useRef<number | null>(null)

  const stopPolling = React.useCallback(() => {
    if (pollRef.current !== null) {
      window.clearTimeout(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const loadLegal = React.useCallback(async () => {
    try {
      setLegalComplete((await fetchActivationPackage("HRIS")).complete)
    } catch {
      setLegalComplete(false)
    }
  }, [])

  const loadHistory = React.useCallback(async (connectionId: number) => {
    const { runs } = await fetchRuns(connectionId)
    setHistory(runs)
    return runs
  }, [])

  const follow = React.useCallback(
    (connectionId: number, runId: number) => {
      stopPolling()
      const tick = async () => {
        try {
          const current = await fetchRun(connectionId, runId)
          setRun(current)
          if (current.status === "running") {
            pollRef.current = window.setTimeout(tick, POLL_MS)
            return
          }
          const { connections } = await fetchConnections()
          setConnection(connections.find((c) => c.id === connectionId) ?? null)
          await loadHistory(connectionId)
        } catch (e) {
          setError((e as Error).message)
        }
      }
      void tick()
    },
    [loadHistory, stopPolling],
  )

  const reload = React.useCallback(async () => {
    setError(null)
    try {
      const { connections } = await fetchConnections()
      const current = connections.find((c) => c.provider === provider) ?? null
      setConnection(current)
      await loadLegal()
      if (current) {
        const runs = await loadHistory(current.id)
        if (runs[0]) follow(current.id, runs[0].id)
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setIsLoading(false)
    }
  }, [follow, loadHistory, loadLegal, provider])

  React.useEffect(() => {
    void reload()
    return stopPolling
  }, [reload, stopPolling])

  const start = React.useCallback(
    async (kind: "preview" | "sync", confirmArchives = false) => {
      if (!connection) return
      const started = kind === "preview" ? await startPreview(connection.id) : await startSync(connection.id, confirmArchives)
      setRun(started)
      follow(connection.id, started.id)
    },
    [connection, follow],
  )

  return {
    isLoading,
    error,
    connection,
    setConnection,
    run,
    history,
    legalComplete,
    reload,
    reloadLegal: loadLegal,
    start,
  }
}
